import { Subscription } from 'rxjs';
import { AggregateDatadogLogger } from '@tv2-player/datadog-logger';
import { EmitableUnifiedPlayer } from '../../types/EmitableUnifiedPlayer';
import { ObservableUnifiedPlayer } from '../../types/ObservableUnifiedPlayer';
import {
  AdBeginEventArgs,
  AdBreakBeginEventArgs,
  AdBreakEndEventArgs,
  AdEndEventArgs,
  TypedEventReturnTypes,
} from '../../core/typedEvents';
import { AD_BEGIN, AD_BREAKBEGIN, AD_BREAKEND, AD_END, MEDIA_TIMEUPDATE } from '../../core/events';

type RobustUnifiedPlayerAdBreakEmitterOptions = {
  /**
   * If true, the ad break will be forced to end if it takes too long.
   */
  forceStopAdBreakEnabled: boolean;
};

/**
 * This class is responsible for forwarding emitting-events when ad breaks are started and ended.
 * It sits between the player and the Yospace plugin to iron out some of the quirks in the Yospace plugin.
 */
export class RobustUnifiedPlayerAdBreakEmitter implements EmitableUnifiedPlayer {
  #thresholdToAutoEndAdBreakInSeconds = 5;
  #player;
  #adBreakForceCloseSubscription: Subscription | null = null;
  #currentAd: AdBeginEventArgs | null = null;

  readonly #forceStopEnabled: boolean = false;

  constructor(
    player: ObservableUnifiedPlayer & EmitableUnifiedPlayer,
    { forceStopAdBreakEnabled }: RobustUnifiedPlayerAdBreakEmitterOptions
  ) {
    this.#player = player;
    this.#forceStopEnabled = forceStopAdBreakEnabled;
  }

  emit<T extends keyof TypedEventReturnTypes>(
    type: T,
    data?: TypedEventReturnTypes[T] | undefined
  ): boolean;

  emit(type: string | symbol, data?: any): boolean;

  emit(type: string | symbol, data?: any): boolean {
    switch (type) {
      case AD_BEGIN:
        return this.onAdBegin(data as AdBeginEventArgs);
      case AD_END:
        return this.onAdEnd(data as AdEndEventArgs);
      case AD_BREAKBEGIN:
        return this.onAdBreakBegin(data as AdBreakBeginEventArgs);
      case AD_BREAKEND:
        return this.onAdBreakEnd(data as AdBreakEndEventArgs);
      default:
        throw new Error('Method not implemented.');
    }
  }

  private emitAdEnd(args: AdEndEventArgs) {
    this.#currentAd = null;
    return this.#player.emit(AD_END, args);
  }

  private onAdBegin(args: AdBeginEventArgs) {
    this.#currentAd = args;
    return this.#player.emit(AD_BEGIN, args);
  }

  private onAdEnd(args: AdEndEventArgs) {
    return this.emitAdEnd(args);
  }

  private onAdBreakBegin(args: AdBreakBeginEventArgs) {
    this.#currentAd = null;
    const adBreakStartTime = this.#player.currentTime;

    // listen for time updates and fire an event if the ad break is taking too long
    this.#adBreakForceCloseSubscription = this.#player
      .observe(MEDIA_TIMEUPDATE)
      .subscribe(event => {
        if (
          args.podDuration &&
          event.currentTime &&
          event.currentTime >
            adBreakStartTime + args.podDuration / 1000 + this.#thresholdToAutoEndAdBreakInSeconds
        ) {
          if (this.#forceStopEnabled) {
            if (this.#currentAd) {
              this.emitAdEnd({ type: AD_END });
            }

            this.#player.emit(AD_BREAKEND, args);

            const logger = new AggregateDatadogLogger();
            // Log a warning that the ad break took too long using our ServiceContainer
            logger.warn('Ad break took too long, forcing it to stop', {
              event,
            });
            // eslint-disable-next-line no-console
            console.warn('Ad break took too long, forcing it to stop', {
              event,
            });
          } else {
            const logger = new AggregateDatadogLogger();
            // Log a warning that the ad break took too long using our ServiceContainer
            logger.warn(
              'Ad break took too long, would force it to stop, but the automatic stopping is disabled',
              {
                event,
              }
            );
          }

          this.#adBreakForceCloseSubscription?.unsubscribe();
        }
      });

    return this.#player.emit(AD_BREAKBEGIN, args);
  }

  private onAdBreakEnd(args: AdBreakEndEventArgs) {
    if (this.#adBreakForceCloseSubscription) {
      this.#adBreakForceCloseSubscription.unsubscribe();
      this.#adBreakForceCloseSubscription = null;
    }

    if (this.#currentAd) {
      this.emitAdEnd({ type: AD_END });
    }

    return this.#player.emit(AD_BREAKEND, args);
  }

  destroy() {
    if (this.#adBreakForceCloseSubscription) {
      this.#adBreakForceCloseSubscription.unsubscribe();
      this.#adBreakForceCloseSubscription = null;
    }
  }
}
